import { httpClient } from "../httpClient";

/**
 * @typedef {Object} ServiceRequestListParams
 * @property {string} [status]
 * @property {string} [seen] 'true' | 'false'
 * @property {string} [q]
 * @property {string} [from]
 * @property {string} [to]
 * @property {string} [aiStatus]
 * @property {string} [sort] e.g. createdAt:desc
 * @property {number} [page]
 * @property {number} [limit]
 */

/**
 * GET /admin/service-requests
 * @param {ServiceRequestListParams} params
 */
export function listServiceRequests(params = {}) {
  return httpClient.get("/admin/service-requests", { params });
}

/**
 * GET /admin/service-requests/:id
 * @param {string} id
 */
export function getServiceRequest(id) {
  return httpClient.get(`/admin/service-requests/${encodeURIComponent(id)}`);
}

/**
 * PATCH /admin/service-requests/:id
 * @param {string} id
 * @param {Record<string, unknown>} body
 */
export function patchServiceRequest(id, body) {
  return httpClient.patch(
    `/admin/service-requests/${encodeURIComponent(id)}`,
    body
  );
}

/**
 * DELETE /admin/service-requests/:id
 * @param {string} id
 */
export function deleteServiceRequest(id) {
  return httpClient.delete(
    `/admin/service-requests/${encodeURIComponent(id)}`
  );
}

/**
 * POST /admin/service-requests/:id/replies
 * @param {string} id
 * @param {{ body: string, internal?: boolean }} body
 */
export function postServiceRequestReply(id, body) {
  return httpClient.post(
    `/admin/service-requests/${encodeURIComponent(id)}/replies`,
    body
  );
}

/**
 * POST /admin/service-requests/:id/emails — send client-visible email (backend delivers via provider).
 * @param {string} id
 * @param {{ subject: string, body: string }} body
 */
export function postServiceRequestEmail(id, body) {
  return httpClient.post(
    `/admin/service-requests/${encodeURIComponent(id)}/emails`,
    body
  );
}

/**
 * GET /admin/service-requests/:id/emails — thread only (same EmailMessageDTOs as detail `emailMessages`).
 * Response: `{ items: EmailMessageDTO[] }` sorted by `createdAt` ascending.
 * @param {string} id
 */
export function getServiceRequestEmails(id) {
  return httpClient.get(
    `/admin/service-requests/${encodeURIComponent(id)}/emails`
  );
}

/**
 * GET /admin/service-requests/:id/email-analysis/history — analysis steps only (no full email bodies).
 * Response: `{ requestId, client, numberOfEmailsAnalysed, totalInboundEmails, history: EmailAnalysisHistoryStepDTO[] }`
 * Each history item may include a nested `clientAnalysis` **or** the same analysis fields flattened on the step; optional `professionalSuggestion` / `budgetAssessment` are read from either place (see `rawClientAnalysisFromEmailAnalysisStep`).
 * @param {string} id
 */
export function getServiceRequestEmailAnalysisHistory(id) {
  return httpClient.get(
    `/admin/service-requests/${encodeURIComponent(id)}/email-analysis/history`
  );
}

/**
 * GET /admin/service-requests/email-analysis/requests — request-level analysis overview list.
 * Response: `{ items: [{ requestId, client, numberOfEmailsAnalysed, totalInboundEmails }] }`
 */
export function listEmailAnalysisRequests() {
  return httpClient.get("/admin/service-requests/email-analysis/requests");
}

/**
 * POST /admin/service-requests/:parentId/emails/:emailMessageId/analysis/run
 * @param {string} parentId
 * @param {string} emailMessageId
 * @param {{ instructionNote?: string, locale?: string }} [body]
 */
export function postServiceRequestEmailAnalysisRun(parentId, emailMessageId, body = {}) {
  return httpClient.post(
    `/admin/service-requests/${encodeURIComponent(parentId)}/emails/${encodeURIComponent(emailMessageId)}/analysis/run`,
    body
  );
}

/**
 * POST /admin/service-requests/:id/notes
 * @param {string} id
 * @param {{ body: string }} body
 */
export function postServiceRequestNote(id, body) {
  return httpClient.post(
    `/admin/service-requests/${encodeURIComponent(id)}/notes`,
    body
  );
}

/**
 * DELETE /admin/service-requests/:id/notes/:noteId
 */
export function deleteServiceRequestNote(id, noteId) {
  return httpClient.delete(
    `/admin/service-requests/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`
  );
}

/**
 * POST /admin/service-requests/:id/analysis/run
 * @param {string} id
 * @param {{ instructionNote?: string } | Record<string, unknown>} [body]
 */
export function runServiceRequestAnalysis(id, body) {
  return httpClient.post(
    `/admin/service-requests/${encodeURIComponent(id)}/analysis/run`,
    body || {}
  );
}

/**
 * POST /public/service-requests — no Bearer (use skipAuth)
 * @param {Record<string, unknown>} body
 */
export function publicCreateServiceRequest(body) {
  return httpClient.post("/public/service-requests", body, { skipAuth: true });
}
